import { motion } from "framer-motion";
import { Sparkles, Users, Megaphone, HandHeart } from "lucide-react";
import { TextReveal } from "@/components/TextReveal";
import { InfoTip } from "@/components/InfoTip";
import {
  PILLAR_RETE,
  PILLAR_VISIBILITA,
  PILLAR_SOSTEGNO,
  PILLAR_INNOVAZIONE,
} from "@/data/media";

const ITEMS = [
  {
    icon: Users,
    title: "Rete",
    image: PILLAR_RETE,
    text: "Imprenditrici che si conoscono davvero: incontri, tavoli di lavoro e collaborazioni concrete.",
    tip: "Ogni nuova socia viene presentata alla rete e messa in contatto con chi opera in settori compatibili.",
  },
  {
    icon: Megaphone,
    title: "Visibilità",
    image: PILLAR_VISIBILITA,
    text: "Ogni socia e ogni sponsor entra nel racconto social della rete, su Instagram.",
    tip: "Contenuti dedicati sul profilo Instagram di DonnaImpresa e presenza nella vetrina del sito, con link diretto alla tua attività.",
  },
  {
    icon: HandHeart,
    title: "Sostegno",
    image: PILLAR_SOSTEGNO,
    text: "Mentorship, formazione e affiancamento nelle fasi chiave della crescita d'impresa.",
    tip: "Le socie con più esperienza affiancano chi sta iniziando: sessioni 1:1 su strategia, finanza e comunicazione.",
  },
  {
    icon: Sparkles,
    title: "Innovazione",
    image: PILLAR_INNOVAZIONE,
    text: "Strumenti digitali e AI per raccontare il proprio lavoro con un'identità forte.",
    tip: "Dal sito al profilo social fino agli strumenti di intelligenza artificiale: ti aiutiamo a costruire una presenza digitale coerente e riconoscibile.",
  },
];

const slug = (s) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export const Manifesto = () => (
  <section
    id="manifesto"
    className="relative mx-auto max-w-[1400px] px-5 py-16 sm:px-10 sm:py-20 lg:py-28"
    data-testid="manifesto-section"
  >
    <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E0708A]">
          Il manifesto
        </p>
        <TextReveal
          text="Fare impresa insieme | è un atto di visione."
          as="h1"
          testId="manifesto-heading"
          className="mt-5 font-display text-4xl leading-tight tracking-tight text-[#1A1617] sm:text-5xl"
        />
        <p className="mt-6 max-w-md text-base leading-relaxed text-[#6B5E62]">
          Nasciamo per dare voce e spazio alle imprese guidate da donne. Non un
          club chiuso, ma una piattaforma aperta dove competenze, storie e
          aziende sostenitrici si incontrano.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        {ITEMS.map((it, i) => (
          <motion.div
            key={it.title}
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.55, delay: i * 0.08 }}
            data-testid={`manifesto-card-${slug(it.title)}`}
            className="group overflow-hidden rounded-2xl border border-[#E8E2E4] bg-white shadow-[0_8px_32px_rgba(94,42,82,0.05)] transition-transform duration-500 hover:-translate-y-1.5"
          >
            <div className="relative h-40 overflow-hidden sm:h-44">
              <img
                src={it.image}
                alt={`Visual astratto: ${it.title}`}
                data-testid={`manifesto-image-${slug(it.title)}`}
                className="h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.08]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3E1B36]/70 via-transparent to-transparent" />
              <span className="absolute left-5 top-5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/85 text-[#5E2A52] backdrop-blur-md transition-colors duration-500 group-hover:bg-[#E0708A] group-hover:text-white">
                <it.icon size={16} />
              </span>
              <h3 className="absolute bottom-4 left-5 font-display text-2xl text-white">
                {it.title}
              </h3>
            </div>
            <div className="p-6">
              <p className="flex items-start gap-1.5 text-sm leading-relaxed text-[#6B5E62]">
                <span>{it.text}</span>
                <InfoTip
                  text={it.tip}
                  testId={`tip-manifesto-${slug(it.title)}`}
                />
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
